<template>
    <div>
        <h1 v-if="translations.nav_admin_subscription">
            {{ translations.nav_admin_subscription }}
        </h1>

        <div class="q-mb-md">
            <div class="text-h6">{{ translations.title }}</div>
            <div v-if="error" class="text-negative q-mb-md">{{ error }}</div>
        </div>

        <div v-if="alreadyInGroup.length > 0" class="q-mb-md">
            <q-banner class="bg-info text-white rounded-borders" rounded>
                <template v-slot:avatar>
                    <q-icon name="info" color="white" />
                </template>
                <div class="text-h6 q-mb-sm">
                    {{ translations.already_covered }}
                </div>
                <ul>
                    <li v-for="item in alreadyInGroup" :key="item.calendarName">
                        <strong>{{ item.calendarName }}</strong>
                        ({{ item.payerName }})
                    </li>
                </ul>
                <div
                    v-if="
                        calendars.length === 0 && !paymentGroup?.paymentGroupId
                    "
                    class="q-mt-md"
                >
                    {{ translations.all_covered }}
                </div>
            </q-banner>
        </div>

        <div v-if="hasExistingPaymentGroup && step === 1" class="q-mb-md">
            <q-banner class="bg-negative text-white rounded-borders" rounded>
                <template v-slot:avatar>
                    <q-icon name="error" color="white" />
                </template>
                <div class="text-h6">
                    {{ translations.error_payment_group_exists }}
                </div>
            </q-banner>
        </div>

        <q-stepper
            v-if="
                calendars.length > 0 ||
                paymentGroup?.paymentGroupId ||
                hasExistingPaymentGroup
            "
            v-model="currentStep"
            color="primary"
            animated
            flat
            bordered
            :vertical="$q.screen.lt.sm"
        >
            <q-step
                name="1"
                :title="translations.step1_title"
                icon="group_add"
                :done="step > 1"
            >
                <div v-if="step === 1">
                    <div v-if="hasExistingPaymentGroup">
                        <p>{{ translations.step1_existing_group_desc }}</p>
                        <q-btn
                            color="primary"
                            :label="translations.edit_group_title"
                            :href="routes.editGroup"
                        ></q-btn>
                    </div>
                    <div v-else>
                        <p>{{ translations.step1_desc }}</p>

                        <form method="POST" :action="routes.storeGroup">
                            <input
                                type="hidden"
                                name="_token"
                                :value="csrfToken"
                            />
                            <div class="q-mb-md">
                                <div
                                    v-for="calendar in calendars"
                                    :key="calendar.calendarId"
                                >
                                    <q-checkbox
                                        v-model="selectedCalendars"
                                        :val="calendar.calendarId"
                                        :label="calendar.calendarName"
                                    />
                                </div>
                            </div>
                            <select
                                v-show="false"
                                multiple
                                name="calendarIds[]"
                            >
                                <option
                                    v-for="id in selectedCalendars"
                                    :key="id"
                                    :value="id"
                                    selected
                                >
                                    {{ id }}
                                </option>
                            </select>

                            <div
                                v-if="validationErrors.calendarIds"
                                class="text-negative q-mb-md"
                            >
                                {{ validationErrors.calendarIds[0] }}
                            </div>

                            <q-btn
                                type="submit"
                                color="primary"
                                :label="translations.step1_btn"
                                :disable="selectedCalendars.length === 0"
                            ></q-btn>
                        </form>
                    </div>
                </div>
            </q-step>

            <q-step
                name="2"
                :title="translations.step2_title"
                icon="payment"
                :done="step > 2"
            >
                <div v-if="step === 2">
                    <p>{{ translations.step2_desc }}</p>
                    <p>{{ translations.step2_plan_features_info }}</p>
                    <form method="POST" :action="routes.storeDecision">
                        <input type="hidden" name="_token" :value="csrfToken" />
                        <div class="q-mb-md">
                            <div>
                                <q-radio
                                    v-model="selectedPlan"
                                    val="monthly"
                                    :label="translations.step2_monthly"
                                />
                            </div>
                            <div>
                                <q-radio
                                    v-model="selectedPlan"
                                    val="yearly"
                                    :label="translations.step2_yearly"
                                />
                            </div>
                            <div>
                                <q-radio
                                    v-model="selectedPlan"
                                    val="free"
                                    :label="translations.step2_free"
                                />
                            </div>
                        </div>
                        <input
                            type="hidden"
                            name="plan"
                            :value="selectedPlan"
                        />

                        <div
                            v-if="validationErrors.plan"
                            class="text-negative q-mb-md"
                        >
                            {{ validationErrors.plan[0] }}
                        </div>

                        <q-btn
                            type="submit"
                            color="primary"
                            :label="translations.step2_save"
                            :disable="!selectedPlan"
                        ></q-btn>
                    </form>
                    <div class="q-mt-md">
                        <form method="POST" :action="routes.backToStep1">
                            <input
                                type="hidden"
                                name="_token"
                                :value="csrfToken"
                            />
                            <q-btn
                                type="submit"
                                color="secondary"
                                outline
                                :label="translations.step2_back"
                            ></q-btn>
                        </form>
                    </div>
                </div>
            </q-step>

            <q-step
                name="3"
                :title="translations.step3_title"
                icon="shopping_cart"
                :done="step === 3"
            >
                <div v-if="step === 3">
                    <div v-if="paymentGroup.plan === 'free'">
                        <p>{{ translations.step3_free_desc }}</p>
                        <div class="q-mt-md">
                            <q-btn
                                color="primary"
                                :label="translations.step3_go_dashboard"
                                :href="routes.index"
                            ></q-btn>
                        </div>
                    </div>
                    <div v-else-if="checkout">
                        <p>
                            {{ translations.step3_paid_desc_prefix }}
                            <strong>{{ capitalizedPlan }}</strong>
                            {{ translations.step3_paid_desc_suffix }}
                        </p>
                        <div>
                            <paddle-checkout
                                :checkout="checkout"
                                :client-token="paddleClientToken"
                                :environment="paddleEnvironment"
                                extra-class="q-btn q-btn-item non-selectable no-outline q-btn--standard q-btn--rectangle bg-primary text-white q-btn--actionable q-focusable q-hoverable"
                            >
                                {{ translations.step3_checkout }}
                            </paddle-checkout>
                        </div>
                    </div>
                    <div v-else>
                        <p>
                            {{ translations.step3_success_prefix }}
                            {{ capitalizedPlan }}
                            {{ translations.step3_success_suffix }}
                        </p>
                        <div class="q-mt-md">
                            <q-btn
                                color="primary"
                                :label="translations.step3_go_dashboard"
                                :href="routes.index"
                            ></q-btn>
                        </div>
                    </div>
                    <div class="q-mt-md">
                        <form method="POST" :action="routes.backToStep2">
                            <input
                                type="hidden"
                                name="_token"
                                :value="csrfToken"
                            />
                            <q-btn
                                type="submit"
                                color="secondary"
                                outline
                                :label="translations.step2_back"
                            ></q-btn>
                        </form>
                    </div>
                </div>
            </q-step>
        </q-stepper>
    </div>
</template>

<script setup>
import { ref, computed } from "vue";
import { useQuasar } from "quasar";
import PaddleCheckout from "./PaddleCheckout.vue";

const props = defineProps({
    step: {
        type: Number,
        required: true,
    },
    calendars: {
        type: Array,
        default: () => [],
    },
    alreadyInGroup: {
        type: Array,
        default: () => [],
    },
    paymentGroup: {
        type: Object,
        default: () => ({}),
    },
    checkout: {
        type: Object,
        default: null,
    },
    routes: {
        type: Object,
        required: true,
    },
    translations: {
        type: Object,
        required: true,
    },
    hasExistingPaymentGroup: {
        type: Boolean,
        default: false,
    },
    csrfToken: {
        type: String,
        required: true,
    },
    paddleClientToken: {
        type: String,
        required: true,
    },
    paddleEnvironment: {
        type: String,
        default: "sandbox",
    },
    error: {
        type: String,
        default: "",
    },
    validationErrors: {
        type: Object,
        default: () => ({}),
    },
});

const $q = useQuasar();
const currentStep = ref(props.step.toString());
const selectedCalendars = ref([]);
const selectedPlan = ref(props.paymentGroup?.plan || null);

const capitalizedPlan = computed(() => {
    const plan = props.paymentGroup?.plan;
    return plan ? plan.charAt(0).toUpperCase() + plan.slice(1) : "";
});
</script>
